<template>
  <div class="flex h-full min-w-0 flex-col gap-2">
    <LoadingSpinner v-if="loading" class="m-auto size-6" />
    <PAlert v-else-if="error" :type="AlertTypes.ERROR">{{ error }}</PAlert>
    <template v-else-if="document">
      <div class="min-h-0 min-w-0 flex-1">
        <DocumentRenderer @loading-complete="emit('loadingComplete')">
          <template v-for="file in document.documents" :key="file.name">
            <DocumentPage
              v-for="(page, index) in file.pages"
              :key="`${file.name}-${index}`"
              class="p-0!"
            >
              <img
                :src="`data:image/png;base64,${page}`"
                :alt="`${file.name}, pagina ${index + 1}`"
                class="block h-auto w-full"
              />
            </DocumentPage>
          </template>
        </DocumentRenderer>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue"
import DocumentRenderer from "@/components/document/DocumentRenderer.vue"
import DocumentPage from "@/components/document/DocumentPage.vue"
import LoadingSpinner from "@/components/loading/LoadingSpinner.vue"
import PAlert from "@/components/elements/PAlert.vue"
import { AlertTypes } from "@/interfaces/component/alert.interface"
import { CustomerPurchaseOrderRepository } from "@/repositories/customer-purchase-order-repository"
import { FileViewerController } from "@/controllers/file-viewer-controller"
import { FileRelation } from "@/models/file/file-relation.model"
import type { ICustomerInvoiceDocument } from "@/interfaces/api/customer-invoice.interface"

const props = defineProps<{ approvalId: string; refreshKey: number; savedInvoiceNote: string }>()
const emit = defineEmits(["loadingComplete"])
const document = ref<ICustomerInvoiceDocument>()
const loading = ref(false)
const error = ref("")
watch(
  () => [props.approvalId, props.refreshKey, props.savedInvoiceNote] as const,
  async ([id], _, onCleanup) => {
    let active = true
    onCleanup(() => {
      active = false
    })
    loading.value = true
    document.value = undefined
    error.value = ""
    try {
      const result = await new CustomerPurchaseOrderRepository().invoiceDocument(id)
      if (!active) return
      document.value = result
      if (result.files.length) {
        const files = result.files.map((file) => new FileRelation(file))
        await FileViewerController.displayFileRelationsInColumn(files, files[0], {
          allowDelete: false,
          createColumnIfNone: true
        })
      }
    } catch (cause) {
      if (active)
        error.value = cause instanceof Error ? cause.message : "De factuur kon niet worden geladen."
    } finally {
      if (active) {
        loading.value = false
        emit("loadingComplete")
      }
    }
  },
  { immediate: true }
)
</script>
